import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { INotification } from './notification.interface';
import { INotificationOptions } from './notification-options.interface';
import { NotificationTypeEnum } from './notification-type.enum';
import { DelayService } from '@trademe/tangram';
import { getErrorMessage } from '@classifieds-sell/shared';
import { NEW_RELIC_SERVICE_TOKEN } from 'libs/trademe-core-ui/src/lib/new-relic/new-relic-token';
import { INewRelicService } from 'libs/trademe-core-ui/src/lib/new-relic/new-relic-service.interface';

export const DEFAULT_NOTIFICATION_DURATION = 5800;

const equivalentNotification =
    (notification: INotification) => (other: INotification) => {
        return (
            notification.type === other.type &&
            notification.title === other.title &&
            notification.message === other.message
        );
    };

const optionsOrDefault = (
    messageOrOptions: INotificationOptions | string
): INotificationOptions => {
    const options =
        typeof messageOrOptions === 'string'
            ? {
                  message: messageOrOptions,
                  duration: DEFAULT_NOTIFICATION_DURATION
              }
            : messageOrOptions;

    if (options.message instanceof Error) {
        options.message = getErrorMessage(options.message);
    }

    if (options.isSticky) {
        options.duration = null;
        options.action = {
            text: 'Close',
            onClick: () => {
                // Do nothing
            }
        };
    }

    if (options.duration === undefined) {
        options.duration = DEFAULT_NOTIFICATION_DURATION;
    }

    return options;
};

type ErrorMessage = string | Error;

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    public notifications$: BehaviorSubject<Array<INotification>>;
    public notifications: Array<INotification> = [];

    constructor(
        @Inject(NEW_RELIC_SERVICE_TOKEN)
        private _newRelicService: INewRelicService,
        private _delayService: DelayService
    ) {
        this.notifications$ = new BehaviorSubject<Array<INotification>>([]);
    }

    public success(
        messageOrOptions: string | INotificationOptions
    ): INotification {
        const options = optionsOrDefault(messageOrOptions);
        return this.addNotification(NotificationTypeEnum.success, options);
    }

    public info(
        messageOrOptions: string | INotificationOptions
    ): INotification {
        const options = optionsOrDefault(messageOrOptions);
        return this.addNotification(NotificationTypeEnum.primary, options);
    }

    public warning(
        messageOrOptions: string | INotificationOptions
    ): INotification {
        const options = optionsOrDefault(messageOrOptions);
        return this.addNotification(NotificationTypeEnum.warning, options);
    }

    /**
     * This method determines how to handle error messages or notifications.
     * Unfortunately the role of New Relic functionality has being directly embedded in here legacy wise and thus we
     * have to handle here, how we inform new relic about whether to "notice" errors so that the SLO's
     * (Service Level Objective) can decide what to do about those specific messages.
     *
     * There are specific user input error messages which we don't think should be reflected in the SLO error rate for
     * this web application but still get recorded as errors in New Relic.  So we've created a new method in the
     * New Relic service class which centralises and encapsulates what error messages we want the SLO to ignore.
     *
     * This approach means the developers need to understand that they may need to update this collection of
     * "noticeable" errors when new ones are added to stop the SLO from needlessly regressing and alerting us.
     *
     * @param messageOrOptions
     */
    public error(
        messageOrOptions: ErrorMessage | INotificationOptions
    ): INotification {
        if (messageOrOptions instanceof Error) {
            messageOrOptions = getErrorMessage(messageOrOptions);
        }

        const options = optionsOrDefault(messageOrOptions);
        const isExpected = this._newRelicService.isExpected(options.message);
        this._newRelicService.noticeError(options.message, isExpected);
        return this.addNotification(NotificationTypeEnum.danger, options);
    }

    public dismiss = (toDismiss: INotification): void => {
        const toRemove = this.notifications.indexOf(toDismiss);

        if (toRemove >= 0) {
            this.notifications.splice(toRemove, 1);
            this.notifySubscribers();
        }
    };

    private addNotification(
        type: NotificationTypeEnum,
        options: INotificationOptions
    ): INotification {
        const notification = {
            ...options,
            type
        };

        // We should only have at most one notification for each message, rather than filling the screen with the same
        // notification again and again.
        if (this.notifications.find(equivalentNotification(notification))) {
            return notification;
        }

        this.notifications.push(notification);

        if (notification.duration) {
            this._delayService.delay(
                () => this.dismiss(notification),
                notification.duration
            );
        }

        this.notifySubscribers();
        return notification;
    }

    private notifySubscribers(): void {
        this.notifications$.next(this.notifications);
    }
}
